export type RuntimeLink = {
  label: string
  href: string
  labels?: Partial<Record<string, string>>
  children?: RuntimeLink[]
}

export type RuntimeHomeSettings = {
  shopName: string
  headerTagline: string
  activeLocales: string[]
  logoUrl: string
  faviconUrl: string
  primaryColor: string
  contactEmail: string
  contactPhone: string
  headerLinks: RuntimeLink[]
  socialLinks: RuntimeLink[]
  footerLinks: RuntimeLink[]
  footerNote: string
  privacyPolicyPath: string
  cookiePolicyPath: string
  termsConditionsUrl: string
}

export const DEFAULT_RUNTIME_HOME_SETTINGS: RuntimeHomeSettings = {
  shopName: '',
  headerTagline: '',
  activeLocales: ['it-IT'],
  logoUrl: '',
  faviconUrl: '',
  primaryColor: '',
  contactEmail: '',
  contactPhone: '',
  headerLinks: [],
  socialLinks: [],
  footerLinks: [],
  footerNote: '',
  privacyPolicyPath: '',
  cookiePolicyPath: '',
  termsConditionsUrl: '',
}

const toTrimmedString = (value: unknown) => (typeof value === 'string' ? value.trim() : '')

const toLocalizedLabelsMap = (value: unknown): Partial<Record<string, string>> | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined

  const normalizedEntries = Object.entries(value as Record<string, unknown>)
    .map(([rawLocale, rawLabel]) => {
      const locale = rawLocale.trim()
      const label = toTrimmedString(rawLabel)
      return [locale, label] as const
    })
    .filter(([locale, label]) => locale.length > 0 && label.length > 0)

  if (!normalizedEntries.length) return undefined
  return Object.fromEntries(normalizedEntries)
}

const LOCALE_CODE_REGEX = /^[a-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/i

const canonicalizeLocaleCode = (value: string): string | null => {
  const trimmed = value.trim()
  if (!trimmed || !LOCALE_CODE_REGEX.test(trimmed)) return null
  const parts = trimmed.split('-').filter(Boolean)
  if (!parts.length) return null
  const [language, ...rest] = parts
  return [
    language.toLowerCase(),
    ...rest.map((segment) => (segment.length === 2 ? segment.toUpperCase() : `${segment.charAt(0).toUpperCase()}${segment.slice(1).toLowerCase()}`)),
  ].join('-')
}

const normalizeActiveLocales = (value: unknown) => {
  if (!Array.isArray(value)) return ['it-IT']
  const unique = new Set<string>()
  for (const item of value) {
    if (typeof item !== 'string') continue
    const locale = canonicalizeLocaleCode(item)
    if (locale === 'it-IT') unique.add(locale)
  }
  return unique.size > 0 ? [...unique] : ['it-IT']
}

const normalizeLinks = (value: unknown): RuntimeLink[] => {
  if (!Array.isArray(value)) return []
  return value
    .map((item) => {
      if (!item || typeof item !== 'object') return null
      const raw = item as Record<string, unknown>
      const label = toTrimmedString(raw.label)
      const href = toTrimmedString(raw.href)
      if (!label || !href) return null
      const entry: RuntimeLink = { label, href }
      const labels = toLocalizedLabelsMap(raw.labels)
      if (labels) entry.labels = labels
      const children = normalizeLinks(raw.children)
      if (children.length) {
        entry.children = children
      }
      return entry
    })
    .filter((entry): entry is RuntimeLink => Boolean(entry))
}

const hasLinkHref = (links: RuntimeLink[], href: string) =>
  links.some((link) => link.href.trim().toLowerCase() === href.trim().toLowerCase())

const addLinkIfMissing = (links: RuntimeLink[], label: string, href: string) => {
  if (!href || hasLinkHref(links, href)) return
  links.push({ label, href })
}

export const resolveHomepageSlugFromRuntimeSettings = (rawSettings: unknown, fallback = '/') => {
  if (!rawSettings || typeof rawSettings !== 'object') return fallback
  const rawSlug = toTrimmedString((rawSettings as Record<string, unknown>).homepageSlug)
  if (!rawSlug) return fallback
  if (rawSlug === '/') return '/'
  return rawSlug.replace(/^\/+/, '')
}

export const parseRuntimeHomeSettings = (rawSettings: unknown): RuntimeHomeSettings => {
  const source = rawSettings && typeof rawSettings === 'object' ? (rawSettings as Record<string, unknown>) : {}
  const privacyPolicyPath = toTrimmedString(source.privacyPolicyPath)
  const cookiePolicyPath = toTrimmedString(source.cookiePolicyPath)
  const termsConditionsUrl = toTrimmedString(source.termsConditionsUrl)

  const footerLinks = normalizeLinks(source.footerLinks)
  addLinkIfMissing(footerLinks, 'Privacy', privacyPolicyPath)
  addLinkIfMissing(footerLinks, 'Cookie', cookiePolicyPath)
  addLinkIfMissing(footerLinks, 'Termini', termsConditionsUrl)

  return {
    shopName: toTrimmedString(source.shopName),
    headerTagline: toTrimmedString(source.headerTagline),
    activeLocales: normalizeActiveLocales(source.activeLocales),
    logoUrl: toTrimmedString(source.logoUrl),
    faviconUrl: toTrimmedString(source.faviconUrl),
    primaryColor: toTrimmedString(source.primaryColor),
    contactEmail: toTrimmedString(source.contactEmail),
    contactPhone: toTrimmedString(source.contactPhone),
    headerLinks: normalizeLinks(source.headerLinks),
    socialLinks: normalizeLinks(source.socialLinks),
    footerLinks,
    footerNote: toTrimmedString(source.footerNote),
    privacyPolicyPath,
    cookiePolicyPath,
    termsConditionsUrl,
  }
}

export const resolveRuntimeSiteName = (
  settings: Pick<RuntimeHomeSettings, 'shopName'> | null | undefined,
  fallback = 'Ecommerce',
) => {
  const shopName = toTrimmedString(settings?.shopName)
  return shopName || fallback
}

export const resolveRuntimeFaviconUrl = (
  settings: Pick<RuntimeHomeSettings, 'faviconUrl'> | null | undefined,
  fallback = '/favicon.ico',
) => {
  const faviconUrl = toTrimmedString(settings?.faviconUrl)
  return faviconUrl || fallback
}
